import type {Evaluator} from "@cm/graph-v3"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {visit} from "#template/template-node/visit-helpers"
import {activationVisitorMarker} from "#template/role-markers"

// Wrapper-style operator: holds a sub-primitive node (e.g. PointSet1, LinePoints2, Grid2) as its
// `primitive` field. The concrete dim-variant pins which sub-primitive types are accepted.
export type PrimitiveParameters<TPrimitive> = {
    name: string
    primitive: TPrimitive
}

export abstract class GeometryPrimitiveNode<
    out T extends IndexingDim,
    out TPrimitive extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, PrimitiveParameters<TPrimitive>> {
    get [activationVisitorMarker]() {
        return true as const
    }

    public override onFilterActive(
        context: OnFilterActiveContext,
        evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<PrimitiveParameters<TPrimitive>> {
        visit(this.parameters.primitive, evaluator)
        return super.onFilterActive(context, evaluator)
    }

    public override onCompile(context: OnCompileContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<PrimitiveParameters<TPrimitive>> {
        visit(this.parameters.primitive, evaluator)
        return super.onCompile(context, evaluator)
    }

    protected override hasValidParameters(
        parameters: PrimitiveParameters<TPrimitive>,
    ): parameters is PrimitiveParameters<TPrimitive> & NonNullableParameters<PrimitiveParameters<TPrimitive>> {
        return parameters.primitive !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<PrimitiveParameters<TPrimitive>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {primitive} = parameters
        const [evaluatedPrimitiveValid, evaluatedPrimitiveInvalid] = scope.branch(evaluator.evaluate(primitive))
        return {evaluatedGeometryData: evaluatedPrimitiveValid, invalidator: evaluatedPrimitiveInvalid}
    }
}
